import React, { useState, useEffect, useCallback } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { API_URL } from "../../config.js";
import ConfirmDialog from "../../components/ConfirmDialog.js";
import Notification from "../../components/Notification.js";
import { allowedToView } from "../../utils/PermissionUtils.js";

function Schools() {
    const [schools, setSchools] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [searchTerm, setSearchTerm] = useState("");
    const [isDropdownOpen, setIsDropdownOpen] = useState(null);
    const [isConfirmDialogOpen, setIsConfirmDialogOpen] = useState(false);
    const [selectedSchool, setSelectedSchool] = useState(null);
    const [notification, setNotification] = useState({ type: "", message: "" });
    const [pagination, setPagination] = useState({
        currentPage: 1,
        perPage: 15,
        total: 0,
        lastPage: 1
    });
    const [filters, setFilters] = useState({
        county_id: '',
        school_type: '',
        boarding_type: ''
    });
    const [counties, setCounties] = useState([]);
    const userRole = localStorage.getItem("userRole");
    
    const canEdit = (userRole) => {
        return (allowedToView(userRole, ['admin', 'superadmin', 'missions-coordinator']));
    };

    const navigate = useNavigate();
    const location = useLocation();
    const token = localStorage.getItem("access_token");

    const fetchCounties = useCallback(async () => {
        try {
            const response = await fetch(`${API_URL}/counties`, {
                method: "GET",
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": `Bearer ${token}`,
                },
            });

            if (response.ok) {
                const data = await response.json();
                setCounties(data);
            }
        } catch (error) {
            console.error("Error fetching counties:", error);
        }
    }, [token]);

    const fetchSchools = useCallback(async (page = 1, search = '') => {
        setLoading(true);
    
        try {
            const params = {
                page: page,
                limit: pagination.perPage,
                search: search,
                ...filters
            };
    
            const queryParams = new URLSearchParams(
                Object.fromEntries(Object.entries(params).filter(([_, v]) => v !== ''))
            );
    
            const response = await fetch(`${API_URL}/schools?${queryParams.toString()}`, {
                method: "GET",
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": `Bearer ${token}`,
                },
            });
    
            if (response.ok) {
                const data = await response.json();
                setSchools(data.data);
                setPagination({
                    currentPage: data.current_page,
                    perPage: data.per_page,
                    total: data.total,
                    lastPage: data.last_page
                });
            } else if (response.status === 401) {
                navigate("/logout", { state: { from: location } });
            } else {
                throw new Error("Failed to fetch schools");
            }
        } catch (error) {
            setError("An error occurred while fetching schools.");
            console.error("Error:", error);
        } finally {
            setLoading(false);
        }
    }, [navigate, pagination.perPage, filters, token, location]);    

    const handleFilterChange = (e) => {
        const { name, value } = e.target;
        setFilters(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleSearch = (e) => {
        const value = e.target.value;
        setSearchTerm(value);
        const timer = setTimeout(() => {
            fetchSchools(1, value);
        }, 500);
        return () => clearTimeout(timer);
    };

    const handlePageChange = (page) => {
        fetchSchools(page, searchTerm);
    };

    const handlePerPageChange = (e) => {
        const newPerPage = parseInt(e.target.value);
        setPagination(prev => ({
            ...prev,
            perPage: newPerPage,
            currentPage: 1
        }));
    };

    const toggleDropdown = (index) => {
        if (isDropdownOpen === index) {
            setIsDropdownOpen(null);
        } else {
            setIsDropdownOpen(index);
        }
    };

    const handleDeleteClick = (school) => {
        setSelectedSchool(school);
        setIsConfirmDialogOpen(true);
    };

    const confirmDeleteSchool = async () => {
        if (selectedSchool) {
            try {
                const response = await fetch(`${API_URL}/schools/${selectedSchool.id}`, {
                    method: 'DELETE',
                    headers: {
                        "Content-Type": "application/json",
                        "Authorization": `Bearer ${token}`,
                    },
                });

                if (response.ok) {
                    setNotification({ type: "success", message: 'School deleted successfully' });
                    fetchSchools(); // Refresh the list
                } else if (response.status === 401) {
                    navigate("/logout", { state: { from: location } });
                } else if (response.status === 404) {
                    setNotification({ type: "danger", message: 'School not found.' });
                } else {
                    setNotification({ type: "danger", message: 'There was an error.' });
                    throw new Error('Failed to delete school');
                }
            } catch (error) {
                setNotification({ message: 'An error occurred while deleting the school.', type: 'danger' });
                console.error("Error:", error);
            } finally {
                setIsConfirmDialogOpen(false);
                setSelectedSchool(null);
            }
        }
    };

    const cancelDeleteSchool = () => {
        setIsConfirmDialogOpen(false);
        setSelectedSchool(null);
    };

    const closeNotification = () => {
        setNotification({ type: "", message: "" });
    };

    useEffect(() => {
        fetchCounties();
        fetchSchools();
    }, [fetchCounties, fetchSchools]);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (isDropdownOpen !== null) {
                const dropdown = document.querySelector(`.dropdown.is-active`);
                if (dropdown && !dropdown.contains(event.target)) {
                    setIsDropdownOpen(null);
                }
            }
        };

        document.addEventListener("click", handleClickOutside);
        return () => document.removeEventListener("click", handleClickOutside);
    }, [isDropdownOpen]);

    return (
        <div>
            <main>
                <nav className="level">
                    <div className="level-left">
                        <div className="level-item">
                            <p className="title">Schools</p>
                        </div>
                        <div className="level-item">
                            <div className="field has-addons">
                                <div className="control">
                                    <div className="select">
                                        <select 
                                            name="county_id"
                                            value={filters.county_id}
                                            onChange={handleFilterChange}
                                        >
                                            <option value="">All Counties</option>
                                            {counties.map(county => (
                                                <option key={county.id} value={county.id}>
                                                    {county.name}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                </div>
                                <div className="control">
                                    <div className="select">
                                        <select 
                                            name="school_type"
                                            value={filters.school_type}
                                            onChange={handleFilterChange}
                                        >
                                            <option value="">All Types</option>
                                            <option value="Girls">Girls</option>
                                            <option value="Boys">Boys</option>
                                            <option value="Mixed">Mixed</option>
                                        </select>
                                    </div>
                                </div>
                                <div className="control">
                                    <div className="select">
                                        <select 
                                            name="boarding_type"
                                            value={filters.boarding_type}
                                            onChange={handleFilterChange}
                                        >
                                            <option value="">All Types</option>
                                            <option value="Day">Day</option>
                                            <option value="Boarding">Boarding</option>
                                            <option value="Day and Boarding">Day and Boarding</option>
                                        </select>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="level-right">
                        <div className="level-item">
                            <div className="select">
                                <select 
                                    value={pagination.perPage}
                                    onChange={handlePerPageChange}
                                >
                                    <option value="10">10 per page</option>
                                    <option value="15">15 per page</option>
                                    <option value="30">30 per page</option>
                                    <option value="50">50 per page</option>
                                </select>
                            </div>
                        </div>
                        {canEdit(userRole) && (
                            <p className="level-item">
                                <Link to="add" className="button primary-bg">
                                    <i className="fa fa-plus" aria-hidden="true"></i> &nbsp; New
                                </Link>
                            </p>
                        )}
                    </div>
                </nav>

                {notification.message && (
                    <Notification
                        type={notification.type}
                        message={notification.message}
                        onClose={closeNotification}
                    />
                )}

                <div className="field">
                    <div className="control has-icons-left">
                        <input
                            className="input"
                            type="text"
                            placeholder="Search by school name, county, school type, or boarding type"
                            value={searchTerm}
                            onChange={handleSearch}
                        />
                        <span className="icon is-left">
                            <i className="fas fa-search" />
                        </span>
                    </div>
                </div>

                {loading ? (
                    <div className="has-text-centered py-5">
                        <span className="icon is-large">
                            <i className="fas fa-spinner fa-pulse fa-2x" />
                        </span>
                        <p>Loading schools...</p>
                    </div>
                ) : error ? (
                    <div className="notification is-danger">{error}</div>
                ) : (
                    <>
                        <div className="table-container">
                            <table className="table is-striped is-fullwidth">
                                <thead>
                                    <tr>
                                        <th>#</th>
                                        <th>School Name</th>
                                        <th>County</th>
                                        <th>School Type</th>
                                        <th>Population</th>
                                        <th>Boarding Type</th>
                                        <th>Options</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {schools?.length > 0 ? (
                                        schools.map((school, index) => (
                                            <tr key={school.id}>
                                                <td>{(pagination.currentPage - 1) * pagination.perPage + index + 1}</td>
                                                <td><Link to={`${school.id}/details`} className="default-color">{school.name}</Link></td>
                                                <td>{school.county?.name || "N/A"}</td>
                                                <td>{school.school_type || "N/A"}</td>
                                                <td>{school.population || "N/A"}</td>
                                                <td>{school.boarding_type || "N/A"}</td>
                                                <td>
                                                    <div className={`dropdown is-right ${isDropdownOpen === index ? 'is-active' : ''}`}>
                                                        <div className="dropdown-trigger">
                                                            <button
                                                                className="button is-white"
                                                                aria-haspopup="true"
                                                                aria-controls="dropdown-menu"
                                                                onClick={() => toggleDropdown(index)}
                                                            >
                                                                <span className="icon">
                                                                    <i className="fas fa-ellipsis-v"></i>
                                                                </span>
                                                            </button>
                                                        </div>
                                                        <div className="dropdown-menu" id="dropdown-menu" role="menu">
                                                            <div className="dropdown-content">
                                                                <Link to={`${school.id}/details`} className="dropdown-item">
                                                                    <span className="icon">
                                                                        <i className="fas fa-eye"></i>
                                                                    </span>
                                                                    <span>View</span>
                                                                </Link>
                                                                {canEdit(userRole) && (
                                                                    <>
                                                                        <Link to={`${school.id}/update`} className="dropdown-item">
                                                                            <span className="icon">
                                                                                <i className="fas fa-edit"></i>
                                                                            </span>
                                                                            <span>Edit</span>
                                                                        </Link>
                                                                        <Link className="dropdown-item" onClick={() => handleDeleteClick(school)}>
                                                                            <span className="icon">
                                                                                <i className="fas fa-trash-alt"></i>
                                                                            </span>
                                                                            <span>Remove</span>
                                                                        </Link>
                                                                    </>
                                                                )}
                                                            </div>
                                                        </div>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))
                                    ) : (
                                        <tr>
                                            <td colSpan="7" className="has-text-centered">No schools found</td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>

                        {pagination.lastPage > 1 && (
                            <nav className="pagination is-centered" role="navigation" aria-label="pagination">
                                <button
                                    className="pagination-previous"
                                    onClick={() => handlePageChange(pagination.currentPage - 1)}
                                    disabled={pagination.currentPage === 1}
                                >
                                    Previous
                                </button>
                                <button
                                    className="pagination-next"
                                    onClick={() => handlePageChange(pagination.currentPage + 1)}
                                    disabled={pagination.currentPage === pagination.lastPage}
                                >
                                    Next
                                </button>
                                <ul className="pagination-list">
                                    {Array.from({ length: Math.min(5, pagination.lastPage) }, (_, i) => {
                                        let pageNum;
                                        if (pagination.lastPage <= 5) {
                                            pageNum = i + 1;
                                        } else if (pagination.currentPage <= 3) {
                                            pageNum = i + 1;
                                        } else if (pagination.currentPage >= pagination.lastPage - 2) {
                                            pageNum = pagination.lastPage - 4 + i;
                                        } else {
                                            pageNum = pagination.currentPage - 2 + i;
                                        }

                                        return (
                                            <li key={pageNum}>
                                                <button
                                                    className={`pagination-link ${pagination.currentPage === pageNum ? 'is-current' : ''}`}
                                                    onClick={() => handlePageChange(pageNum)}
                                                >
                                                    {pageNum}
                                                </button>
                                            </li>
                                        );
                                    })}
                                    {pagination.lastPage > 5 && pagination.currentPage < pagination.lastPage - 2 && (
                                        <>
                                            <li><span className="pagination-ellipsis">&hellip;</span></li>
                                            <li>
                                                <button
                                                    className="pagination-link"
                                                    onClick={() => handlePageChange(pagination.lastPage)}
                                                >
                                                    {pagination.lastPage}
                                                </button>
                                            </li>
                                        </>
                                    )}
                                </ul>
                            </nav>
                        )}
                    </>
                )}

                <ConfirmDialog
                    isOpen={isConfirmDialogOpen}
                    theme="is-danger"
                    title="Delete Confirmation"
                    message={`Are you sure you want to delete ${selectedSchool?.name}?`}
                    onConfirm={confirmDeleteSchool}
                    onCancel={cancelDeleteSchool}
                    isLoading={false}
                />
            </main>
        </div>
    );
}

export default Schools;